import type {
  AdminEventRegistration,
  AdminTournamentBracketMatch,
} from "../services/eventRegistrationAdminService";
import type { TournamentOperationCategory } from "./tournamentOperationTypes";

export function categoryLabel(
  categories: TournamentOperationCategory[],
  key?: string | null,
) {
  const normalized = String(key || "").trim();
  const category = categories.find(
    (item) => String(item.categoryKey || "").trim() === normalized,
  );
  return String(category?.categoryName || normalized || "Categoria").trim();
}

export function matchStatusLabel(status?: string | null) {
  const normalized = String(status || "")
    .trim()
    .toLowerCase();
  if (normalized === "finished") return "Finalizado";
  if (normalized === "in_progress") return "Em quadra";
  if (normalized === "scheduled") return "Agendado";
  if (normalized === "pending") return "Agendado";
  return "Pendente";
}

export function isByeMatch(match: {
  decidedBy?: string | null;
  sideAIsBye?: boolean | null;
  sideBIsBye?: boolean | null;
}) {
  return (
    String(match.decidedBy || "").trim().toLowerCase() === "bye" ||
    Boolean(match.sideAIsBye) ||
    Boolean(match.sideBIsBye)
  );
}

export function formatElapsedSince(
  value: string | null | undefined,
  nowMs: number,
) {
  const startedAt = new Date(String(value || "")).getTime();
  if (!Number.isFinite(startedAt)) return "";
  const elapsedMinutes = Math.max(0, Math.floor((nowMs - startedAt) / 60000));
  if (elapsedMinutes < 1) return "Iniciado há menos de 1 min";
  if (elapsedMinutes < 60) return `Iniciado há ${elapsedMinutes} min`;

  const hours = Math.floor(elapsedMinutes / 60);
  const minutes = elapsedMinutes % 60;
  if (hours < 24) {
    return minutes > 0
      ? `Iniciado há ${hours}h ${minutes}min`
      : `Iniciado há ${hours}h`;
  }

  const days = Math.floor(hours / 24);
  const remainingHours = hours % 24;
  return remainingHours > 0
    ? `Iniciado há ${days}d ${remainingHours}h`
    : `Iniciado há ${days}d`;
}

export function inProgressElapsedLabel({
  status,
  actualStartedAt,
  nowMs,
}: {
  status?: string | null;
  actualStartedAt?: string | null;
  nowMs: number;
}) {
  return String(status || "")
    .trim()
    .toLowerCase() === "in_progress"
    ? formatElapsedSince(actualStartedAt, nowMs)
    : "";
}

export function roundLabel(
  roundCode?: string | null,
  roundNumber?: number | null,
) {
  const normalized = String(roundCode || "").toLowerCase();
  if (normalized === "group" || normalized === "group_stage") return "Grupo";
  if (normalized === "play_in") return "Classificatório";
  if (normalized === "round_of_32") return "16 avos de final";
  if (normalized === "round_of_16") return "Oitavas de final";
  if (normalized === "round_of_8" || normalized === "quarterfinal") {
    return "Quartas de final";
  }
  if (normalized === "round_of_4" || normalized === "semifinal") {
    return "Semifinal";
  }
  if (normalized === "round_of_2" || normalized === "final") return "Final";
  return Number(roundNumber || 0) <= 1 ? "Grupo" : "Mata-mata";
}

export function matchCodeForRound(
  roundCode?: string | null,
  matchNumber?: number | null,
) {
  const number = Math.max(1, Math.floor(Number(matchNumber || 1)));
  const normalized = String(roundCode || "").toLowerCase();
  if (normalized === "play_in") return `CL${number}`;
  if (normalized === "round_of_64") return `R64-${number}`;
  if (normalized === "round_of_32") return `R32-${number}`;
  if (normalized === "round_of_16") return `R16-${number}`;
  if (normalized === "round_of_8" || normalized === "quarterfinal") {
    return `QF${number}`;
  }
  if (normalized === "round_of_4" || normalized === "semifinal") {
    return `SF${number}`;
  }
  if (normalized === "round_of_2" || normalized === "final") {
    return `F${number}`;
  }
  return `J${number}`;
}

export function matchWinnerLabel(match: AdminTournamentBracketMatch) {
  if (match.winnerSide === "A") return match.sideALabel || "Dupla A";
  if (match.winnerSide === "B") return match.sideBLabel || "Dupla B";
  return "";
}

export function pairLabelFromRegistration(registration: AdminEventRegistration) {
  const player = String(registration.userName || "Jogador").trim();
  const partner = String(registration.partnerName || "Jogador").trim();
  return `${player} / ${partner}`;
}

export function manualGroupName(index: number) {
  return `Grupo ${String.fromCharCode(65 + index)}`;
}

export function manualGroupId(index: number) {
  return String.fromCharCode(65 + index).toLowerCase();
}
